import { useState } from "react";
import { Search, X, Package, Palette, ChevronDown, ChevronRight } from "lucide-react";
import { img } from "@/lib/media";
import { isEachUnit } from "@/lib/uom";

export const METHODS = ["cash", "card", "cheque", "e_transfer", "wallet", "bank_transfer", "unpaid"];
export const STATUSES = ["draft", "unpaid", "sent", "paid", "partially_paid", "on_account", "refunded", "voided"];
const STATUS_ALIASES = { pending: "unpaid", refund: "refunded", partially_refunded: "refunded" };
export const normStatus = (s) => STATUS_ALIASES[s] || s || "draft";
export const statusLabel = (s) => (s || "").replace(/_/g, " ");
export const phoneValid = (p) => { const d = (p || "").replace(/\D/g, ""); return d.length >= 7 && d.length <= 15 && /^[+]?[\d\s\-().]+$/.test((p || "").trim()); };
export const buildSmsLink = (phone, msg) => `sms:${(phone || "").replace(/[^\d+]/g, "")}?body=${encodeURIComponent(msg)}`;

export const TAGS = [
  { key: "", label: "None", desc: "No status tag applied.", cls: "bg-zinc-100 text-zinc-500", dot: "#a1a1aa" },
  { key: "pickup", label: "Picked Up", desc: "The customer has picked up the order.", cls: "bg-violet-100 text-violet-700", dot: "#7c3aed" },
  { key: "delivered", label: "Delivered", desc: "The order has been delivered.", cls: "bg-emerald-100 text-emerald-700", dot: "#059669" },
  { key: "complete", label: "Completed", desc: "The order is complete.", cls: "bg-blue-100 text-blue-700", dot: "#2563eb" },
];

export const BG_PRESETS = [
  { name: "White", hex: "#ffffff" }, { name: "Light Blue", hex: "#eaf3fb" },
  { name: "Light Gray", hex: "#f3f4f6" }, { name: "Light Green", hex: "#ecfdf5" },
  { name: "Light Yellow", hex: "#fefce8" }, { name: "Light Pink", hex: "#fdf2f8" },
  { name: "Light Purple", hex: "#f5f3ff" }, { name: "Beige", hex: "#f5f5dc" },
];
export const FONT_OPTIONS = ["Inter", "Roboto", "Poppins", "Open Sans", "Lato", "Montserrat", "Arial", "Georgia"];
export const TEXT_PRESETS = [
  { name: "Charcoal", hex: "#1f2d3d" }, { name: "Black", hex: "#000000" },
  { name: "Slate", hex: "#334155" }, { name: "Navy", hex: "#0b2740" },
  { name: "Espresso", hex: "#4b3b2a" }, { name: "Forest", hex: "#14532d" },
];

export function Section({ title, open, onToggle, children, testid, right }) {
  return (
    <div className="border border-zinc-200 rounded-xl overflow-hidden bg-white">
      <button type="button" onClick={onToggle} data-testid={testid} className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-zinc-50">
        <span className="font-semibold text-sm text-zinc-800">{title}</span>
        <span className="flex items-center gap-2">{right}{open ? <ChevronDown size={16} className="text-zinc-400" /> : <ChevronRight size={16} className="text-zinc-400" />}</span>
      </button>
      {open && <div className="p-4 border-t border-zinc-100">{children}</div>}
    </div>
  );
}

export function Fld({ k, label, full, type = "text", value, onChange, idp = "if" }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="text-[10px] uppercase tracking-widest font-bold text-zinc-400">{label}</label>
      <input type={type} data-testid={`${idp}-${k}`} value={value} onChange={onChange} className="inp !h-10 mt-1 text-sm" />
    </div>
  );
}

export function DesignControls({ bg, font, text, onBg, onFont, onText, idp }) {
  const cur = (bg || "#ffffff").toLowerCase();
  const curText = (text || "#1f2d3d").toLowerCase();
  return (
    <div className="space-y-3" data-testid={`${idp}-design-controls`}>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-zinc-400">Header / accent color</label>
        <div className="flex flex-wrap items-center gap-2 mt-1.5">
          {BG_PRESETS.map((p) => (
            <button key={p.hex} type="button" data-testid={`${idp}-bg-${p.name.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => onBg(p.hex)} title={p.name}
              className={`h-8 w-8 rounded-lg border transition active:scale-90 ${cur === p.hex.toLowerCase() ? "border-brand ring-2 ring-brand/40" : "border-zinc-200"}`}
              style={{ background: p.hex }} />
          ))}
          <label className="h-8 w-8 rounded-lg border border-zinc-200 relative overflow-hidden cursor-pointer flex items-center justify-center text-zinc-500" title="Custom color" style={{ background: cur }}>
            <Palette size={14} />
            <input type="color" data-testid={`${idp}-bg-custom`} value={bg || "#ffffff"} onChange={(e) => onBg(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer" />
          </label>
        </div>
      </div>
      {onText && (
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-zinc-400">Body text color</label>
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            {TEXT_PRESETS.map((p) => (
              <button key={p.hex} type="button" data-testid={`${idp}-text-${p.name.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => onText(p.hex)} title={p.name}
                className={`h-8 w-8 rounded-lg border transition active:scale-90 ${curText === p.hex.toLowerCase() ? "border-brand ring-2 ring-brand/40" : "border-zinc-200"}`}
                style={{ background: p.hex }} />
            ))}
            <label className="h-8 w-8 rounded-lg border border-zinc-200 relative overflow-hidden cursor-pointer flex items-center justify-center text-white" title="Custom text color" style={{ background: curText }}>
              <Palette size={14} />
              <input type="color" data-testid={`${idp}-text-custom`} value={text || "#1f2d3d"} onChange={(e) => onText(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer" />
            </label>
          </div>
        </div>
      )}
      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-zinc-400">Font</label>
        <select data-testid={`${idp}-font`} value={font || "Inter"} onChange={(e) => onFont(e.target.value)} className="inp !h-10 mt-1 text-sm">
          {FONT_OPTIONS.map((ff) => <option key={ff} value={ff}>{ff}</option>)}
        </select>
      </div>
    </div>
  );
}

export function VariantSearch({ products, value, onSelect, onClear, onQueryChange, idp = "if" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const rows = [];
  for (const p of products) {
    const alts = p.uoms || [];
    if ((p.base_unit && p.base_unit !== "Each") || alts.length === 0) {
      rows.push({ pid: p.id, name: p.name, unit: isEachUnit(p.base_unit) ? "" : p.base_unit, isBase: true, price: Number(p.price) || 0, part_number: p.part_number || "", brand: p.brand || "", stock: Number(p.stock) || 0, factor: 1, image: (p.images?.[0]?.url) || "" });
    }
    for (const u of alts) {
      const f = Number(u.factor) || 1;
      rows.push({ pid: p.id, name: p.name, unit: u.unit, isBase: false, price: Number(u.price) || 0, part_number: u.part_number || p.part_number || "", brand: u.brand || "", stock: u.in_stock != null ? Number(u.in_stock) : (f > 0 ? Math.floor((p.stock || 0) / f) : 0), factor: f, image: (p.images?.[0]?.url) || "" });
    }
  }
  const ql = q.trim().toLowerCase();
  const filtered = (ql ? rows.filter((r) => `${r.name} ${r.unit} ${r.brand} ${r.part_number}`.toLowerCase().includes(ql)) : rows).slice(0, 40);
  const pick = (r) => { onSelect(r); setQ(""); setOpen(false); if (onQueryChange) onQueryChange(""); };
  return (
    <div className="relative">
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          data-testid={`${idp}-add-search`}
          value={open ? q : (value ? `${value.name} · ${value.isBase ? value.unit : (value.brand || value.unit)}` : q)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); if (value) onClear(); if (onQueryChange) onQueryChange(e.target.value); }}
          onFocus={() => { setOpen(true); if (value) onClear(); }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Search item, SKU, part #, brand or type a name…"
          className="inp !h-9 pl-9 pr-8 text-sm w-full" />
        {(value || q) && <button type="button" data-testid={`${idp}-add-clear`} onMouseDown={(e) => { e.preventDefault(); setQ(""); onClear(); setOpen(false); if (onQueryChange) onQueryChange(""); }} className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded hover:bg-zinc-100 flex items-center justify-center text-zinc-400"><X size={13} /></button>}
      </div>
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-zinc-200 rounded-xl shadow-lg py-1" data-testid={`${idp}-add-results`}>
          {filtered.length === 0 && <p className="px-3 py-3 text-xs text-zinc-400">No matching products — press Add to create a custom line.</p>}
          {filtered.map((r, ridx) => (
            <button
              type="button"
              key={`${r.pid}-${r.unit}-${ridx}`}
              data-testid={`${idp}-add-result-${r.pid}-${r.isBase ? "base" : r.unit}`}
              onMouseDown={(e) => { e.preventDefault(); pick(r); }}
              className="w-full text-left px-3 py-2 hover:bg-zinc-50 flex items-center gap-3">
              <span className="h-8 w-8 rounded-md bg-zinc-100 overflow-hidden shrink-0 flex items-center justify-center text-zinc-300">{r.image ? <img src={img(r.image)} alt="" className="w-full h-full object-cover" /> : <Package size={15} />}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold truncate">{r.name}</span>
                <span className="block text-[11px] text-zinc-400 truncate">{r.isBase ? r.unit : (r.brand || r.unit)}{r.part_number ? ` · ${r.part_number}` : ""}</span>
              </span>
              <span className="text-right shrink-0"><span className="block text-sm font-semibold">${r.price.toFixed(2)}</span><span className="block text-[11px] text-zinc-400">{r.stock} in stock</span></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
